import { Column, Divider, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomImage from "../../../common/components/image/CustomImage";
import { C2Text, C3Text } from "../../../common/components/text/CustomText";
import CustomIcon from "../../../common/components/image/CustomIcon";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import styled from "styled-components";
import { useRef, useState } from "react";
import { useClickOutside } from "../../../common/hooks/useClickOutside";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import DeviceMenu from "./DeviceMenu";
import UIUtil from "../../../common/utils/UIUtil";
import Translate from "../../../common/translate/Translate";
import DeviceSettingModal from "../modals/DeviceSettingModal";
import DeviceMemoModal from "../modals/DeviceMemoModal";
import DeviceDiagnosisModal from "../modals/DeviceDiagnosisModal";
import DeviceHistoryModal from "../modals/DeviceHistoryModal";
import { Theme } from "../../../common/Theme";

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

function DeviceDetailCard({ device }) {
    // 제품 상태
    const { statusTextColor, statusIcon, statusText, buttonText, buttonColor, popupText } = UIUtil.getDeviceStatusUI(
        device.status
    );

    // 메뉴 드롭다운
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    const menuRef = useRef(null);
    useClickOutside(menuRef, () => {
        if (isMenuOpen && !openHistoryModal && !openDiagnosisModal && !openMemoModal && !openSettingModal) {
            setIsMenuOpen(false);
        }
    });

    // 팝업
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const handlePopupClose = () => {
        setIsPopupOpen(false);
    };

    const handlePopupOk = () => {
        setIsPopupOpen(false);
    };

    // history modal
    const [openHistoryModal, setOpenHistoryModal] = useState(false);

    // diagnosis modal
    const [openDiagnosisModal, setOpenDiagnosisModal] = useState(false);

    // memo modal
    const [openMemoModal, setOpenMemoModal] = useState(false);

    // setting modal
    const [openSettingModal, setOpenSettingModal] = useState(false);

    return (
        <SizedBox
            width={"305px"}
            height={"304px"}
            alignItem={"flex-start"}
            justifyContent={"flex-start"}
            borderRadius={"16px"}
            border={`1px solid ${Theme.color.gray200}`}
            overflow={"none"}
        >
            <Row justifyContent={"space-between"} gap={"8px"} width={"100%"} padding={"18px 24px 10px 24px"}>
                <Row flex={"1"} minWidth={"0"}>
                    <CustomImage imageName={`image_${device.type}`} width={"34px"} height={"42px"} />
                    <SizedBox width={"14px"} />
                    <C2Text fontWeight={"500"} flex={"1"} useEllipsis>
                        {device.name}
                    </C2Text>
                </Row>
                <MenuContainer ref={menuRef}>
                    <SizedBox
                        width={"36px"}
                        height={"36px"}
                        border={`1px solid ${Theme.color.gray200}`}
                        borderRadius={"8px"}
                        cursor={"pointer"}
                        onClick={toggleMenu}
                        hoverBackgroundColor={Theme.color.neutral100}
                    >
                        <CustomIcon iconName={"menu"} width={"2.25px"} height={"13.5px"} cursor={"pointer"} />
                    </SizedBox>
                    {isMenuOpen && (
                        <DeviceMenu
                            device={device}
                            onHistoryClick={() => setOpenHistoryModal(true)}
                            onDiagnosisClick={() => setOpenDiagnosisModal(true)}
                            onMemoClick={() => setOpenMemoModal(true)}
                            onSettingClick={() => setOpenSettingModal(true)}
                        />
                    )}
                </MenuContainer>
            </Row>
            <Divider />
            <Column padding={"22px 18px 14px 24px"} width={"100%"} alignItem={"flex-start"}>
                <C3Text fontWeight={"500"}>
                    {Translate("store.deviceDetailCard.status")} / {Translate("store.deviceDetailCard.course")}
                </C3Text>
                <SizedBox height={"6px"} />
                <Row>
                    <CustomIcon iconName={statusIcon} size={"18px"} />
                    <SizedBox width={"4px"} />
                    <C2Text fontColor={statusTextColor}>{statusText}</C2Text>
                    <SizedBox width={"4px"} />
                    {device.status === "inUse" && (
                        <C3Text fontWeight={"500"} fontColor={Theme.color.gray500}>
                            / {device.courseName}
                        </C3Text>
                    )}
                </Row>
                <SizedBox height={"16px"} />
                <C3Text fontWeight={"500"}>{Translate("store.deviceDetailCard.remainingTime")}</C3Text>
                <SizedBox height={"6px"} />
                <C2Text>{device.remainTime ? device.remainTime : "-"}</C2Text>
                <SizedBox height={"16px"} />
                <C3Text fontWeight={"500"} fontColor={Theme.color.gray500}>
                    {Translate("store.deviceDetailCard.cycle")}
                </C3Text>
                <SizedBox height={"6px"} />
                <C2Text>{device.cycle}</C2Text>
                <SizedBox height={"8px"} />
                <Row width={"100%"} justifyContent={"flex-end"}>
                    <SecondaryButton text={buttonText} fontColor={buttonColor} onClick={() => setIsPopupOpen(true)} />
                </Row>
            </Column>
            <CustomPopup
                isOpen={isPopupOpen}
                onClose={handlePopupClose}
                onConfirm={handlePopupOk}
                message={popupText}
            />

            {openHistoryModal && (
                <DeviceHistoryModal
                    isOpen={openHistoryModal}
                    onClose={() => setOpenHistoryModal(false)}
                    device={device}
                />
            )}
            {openDiagnosisModal && (
                <DeviceDiagnosisModal
                    isOpen={openDiagnosisModal}
                    onClose={() => setOpenDiagnosisModal(false)}
                    device={device}
                />
            )}
            {openMemoModal && (
                <DeviceMemoModal isOpen={openMemoModal} onClose={() => setOpenMemoModal(false)} device={device} />
            )}
            {openSettingModal && (
                <DeviceSettingModal
                    isOpen={openSettingModal}
                    onClose={() => setOpenSettingModal(false)}
                    device={device}
                />
            )}
        </SizedBox>
    );
}

export default DeviceDetailCard;
